import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Loader2, HelpCircle } from 'lucide-react';
import AuthBrandPanel from '../components/AuthBrandPanel';
import './AuthPages.css';
import NotificationModal from '../components/NotificationModal';
import { useNotification } from '../hooks/useNotification';

const Login = () => {
  const [searchParams] = useSearchParams();
  const referral = searchParams.get('referral');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [retryUntil, setRetryUntil] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [ended] = useState(() => sessionStorage.getItem("insightSessionEnded"));
  useEffect(() => { sessionStorage.removeItem("insightSessionEnded"); }, []);
  useEffect(() => {
    const update = () => setRemaining(Math.max(0, Math.ceil((retryUntil - Date.now()) / 1000)));
    update();
    const timer = setInterval(update, 250);
    return () => clearInterval(timer);
  }, [retryUntil]);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const { notification, showSuccess, showError, closeNotification } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/login`, { email, password, ...(referral ? { referral_token: referral } : {}) });
      login(res.data.access_token, res.data.user);
      showSuccess('Welcome back!');
      setTimeout(() => {
        navigate(`/${res.data.user.role}-dashboard`);
      }, 1500);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.code === 'SESSION_RECOVERY_PENDING') {
        setRetryUntil(Date.now() + detail.retry_after_seconds * 1000);
      } else {
        showError(typeof detail === 'string' ? detail : detail?.message || 'Login failed');
      }
    } finally {
      setPassword('');
      setLoading(false);
    }
  };

  return (
    <div className="insight-auth insight-auth--login">
      <AuthBrandPanel />
      <main className="insight-auth-main">
      <div className="insight-auth-form" data-testid="login-form">
        <div className="mb-6">
          <Link to="/" className="insight-auth-home">
            ← Back to Home
          </Link>
        </div>
        {referral && <p className="text-center mb-4">Client login · psychologist referral</p>}
        <header className="insight-auth-heading"><h1>Welcome Back</h1><p>Sign in to your INSIGHT professional workspace.</p></header>
        
        {ended && <p role="status" className="mb-4 text-sm">{ended}</p>}
        {retryUntil > 0 && <p role="status" className="mb-4 text-sm">{remaining > 0
          ? `Previous browser session is closing. You can retry in ${remaining} seconds.`
          : 'Recovery wait has ended. Enter your password and sign in again.'}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="login-password-input"
            />
          </div>
          
          <div className="flex justify-end">
            <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="text-sm font-medium hover:underline"
                  style={{ color: '#117F85' }}
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" style={{ color: '#117F85' }} />
                    Forgot Your Password?
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-gray-700">
                    To reset your password, please contact your system administrator.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-900 mb-2">Administrator Contact:</p>
                    <p className="text-sm text-blue-800">
                      Please email or contact your administrator to request a password reset. They will be able to reset your password from the Admin Dashboard.
                    </p>
                  </div>
                  <Button 
                    onClick={() => setShowForgotPassword(false)} 
                    className="w-full"
                    data-testid="forgot-password-close-btn"
                  >
                    Got it
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Button type="submit" className="w-full" disabled={loading || remaining > 0} data-testid="login-submit-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : 'Sign In'}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-gray-600">
          Don't have an account?{' '}
          <Link to={referral ? `/register?referral=${encodeURIComponent(referral)}` : "/register"} className="font-semibold" style={{ color: '#117F85' }} data-testid="login-register-link">
            Register here
          </Link>
        </p>
      </div>
      </main>
      
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default Login;